import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, DatePicker, Input, Select, Space, Table, Tag } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageTitle from 'components/PageTitle'
import { getRequest, inventoryChangesUrl, inventoryPeopleUrl } from '../../config'
import FieldChanges from './FieldChanges'
import HealthStrip from './HealthStrip'
import {
  actionColor,
  actionLabel,
  buildChangesQuery,
  formatDateTime,
  viaLabel
} from './utils'

const { RangePicker } = DatePicker

// The columns are a factory-free constant: nothing here needs to close over page state. antd `render`
// functions return JSX, which the display-name rule reads as an anonymous component; they are cell
// renderers, disabled the same way the rest of the dashboard does it.
/* eslint-disable react/display-name */
const columns = [
  {
    title: 'When',
    key: 'createdAt',
    width: 170,
    render: (_, r) => formatDateTime(r.createdAt)
  },
  {
    title: 'Car',
    key: 'car',
    render: (_, r) =>
      r.car ? <Link to={`/cars/${r.car}`}>{r.carLabel || 'Open car'}</Link> : r.carLabel || '—'
  },
  {
    title: 'What',
    key: 'action',
    width: 190,
    render: (_, r) => <Tag color={actionColor(r.action)}>{actionLabel(r.action)}</Tag>
  },
  {
    title: 'Changed',
    key: 'fieldsChanged',
    render: (_, r) => <FieldChanges changes={r.fieldsChanged} />
  },
  {
    title: 'By',
    key: 'by',
    width: 200,
    render: (_, r) => (
      <div>
        <div>{r.actorName || '—'}</div>
        <div style={{ opacity: 0.6, fontSize: 12 }}>{viaLabel(r.via)}</div>
      </div>
    )
  }
]
/* eslint-enable react/display-name */

// Every change the bot — and the background checks — made to a car: when, which car, who, and the
// before and after of each field. Read-only on purpose: an undo happens in the chat, where the
// person who made the change can see what they are reversing.
const ChangesPage = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [car, setCar] = useState('')
  const [actor, setActor] = useState(undefined)
  const [range, setRange] = useState(null)
  const [people, setPeople] = useState([])

  const load = useCallback(async () => {
    const dates = range ? range.map(m => (m ? m.format('YYYY-MM-DD') : null)) : null
    const query = buildChangesQuery({ car, actor, range: dates, page })
    const response = await getRequest(`${inventoryChangesUrl}${query}`, 'changes', setLoading)
    if (response.success) {
      setRows(response.data.results || [])
      setCount(response.data.count || 0)
    }
  }, [car, actor, range, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    // The people who can act, for the "by" filter. Best-effort: without them the filter is empty.
    getRequest(inventoryPeopleUrl, 'people', () => {}).then(response => {
      if (response.success) {
        const list = response.data.results || response.data
        setPeople(list.map(p => ({ value: p.user, label: p.displayName || p.userName })))
      }
    })
  }, [])

  const reset = (setter, value) => {
    setter(value)
    setPage(1)
  }

  return (
    <div className="container-fluid">
      <PageTitle title="Inventory assistant · Change log" />
      <HealthStrip />
      <Card
        title="What the bot changed"
        extra={
          <Button icon={<ReloadOutlined />} onClick={load}>
            Reload
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            allowClear
            placeholder="Car id"
            style={{ width: 280 }}
            value={car}
            onChange={e => reset(setCar, e.target.value)}
          />
          <Select
            allowClear
            placeholder="By"
            style={{ width: 200 }}
            options={people}
            value={actor}
            onChange={value => reset(setActor, value)}
          />
          <RangePicker value={range} onChange={value => reset(setRange, value)} />
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            total: count,
            pageSize: 50,
            showSizeChanger: false,
            onChange: setPage
          }}
        />
      </Card>
    </div>
  )
}

export default ChangesPage
